import type { Platform } from "../platform/Platform";

/**
 * Endless Growth V1's own, self-contained context/outcome/record model.
 * Deliberately never reuses Daily's types (`DailyGameSceneContext`/
 * `DailyAttemptOutcome`/`DailyResultSceneData` - see
 * `src/daily/DailyRunContext.ts`), which carry Daily-specific dates,
 * attempt numbers, challenge ids, participation, ranking, and reservation
 * fields with no Endless equivalent - Endless is fully local: no
 * reservation, no ranking, no server verification.
 */

/**
 * What `GameScene` needs to start an Endless run. Deliberately just a
 * marker today - unlike Daily (a specific day's spec/attempt) or Campaign
 * (a specific level id from `LevelRegistry`), Endless has exactly one
 * level and no reservation, so there is no real per-run data to carry in.
 * Kept as its own type (not a bare boolean) so `GameScene` can switch on
 * a literal discriminant (`kind === "endless"`) the same way it already
 * does for `dailyContext`, and so a future field never has to be
 * threaded through by hand.
 */
export interface EndlessRunContext {
  readonly kind: "endless";
}

/** The single shared instance every Main Menu ENDLESS button press hands to `GameScene` - see `EndlessRunContext`'s own doc for why there's nothing per-run to carry. */
export const ENDLESS_RUN_CONTEXT: EndlessRunContext = { kind: "endless" };

/**
 * Why an Endless run ended - the three record-eligible endings the
 * product spec locks: a real death, a voluntary END RUN ("quit"), and the
 * exceptional ARENA CLEARED completion. Deliberately never "completed"
 * (Campaign's own star/target-length wording) or "restart"/"menu_exit"
 * (Campaign/Daily's mid-run abandonment wording) - Endless has its own,
 * accurate vocabulary.
 */
export type EndlessEndReason = "death" | "quit" | "arena_cleared";

/** The user-facing result-screen headline for each `EndlessEndReason` - the single source of truth so the result scene never re-derives its own copy from the reason string. */
export const ENDLESS_HEADLINE_BY_REASON: Record<EndlessEndReason, string> = {
  death: "YOU DIED",
  quit: "RUN ENDED",
  arena_cleared: "ARENA CLEARED",
};

/**
 * A completed Endless run's full result - everything the result screen,
 * local best storage, and analytics all need, computed once at the
 * moment the run actually ends (never re-derived later from a stale
 * snapshot).
 */
export interface EndlessRunOutcome {
  reason: EndlessEndReason;
  /** Present only for `reason: "death"` - `null` for "quit"/"arena_cleared", which are never a collision. */
  deathReason: "wall" | "obstacle" | "self" | null;
  length: number;
  score: number;
  /** Active (pause-excluded) survival time, in ms - see the HUD's TIME field/`MM:SS`/`H:MM:SS` formatting. Never raw wall-clock time. */
  survivalTimeMs: number;
  /** The highest RUSH scoring combo (capped at 5, same cap the in-run HUD already shows) reached this run. */
  maxRushCombo: number;
  safeCount: number;
  rushCount: number;
  chaosCount: number;
  /** The fastest (smallest) movement interval actually reached this run, in ms - never below the permanent 75ms Endless cap. */
  fastestStepMs: number;
  /** True only once `EndlessStorage`'s comparator says this run's canonical record actually improved the stored best - see that module's own doc. Always `false` until persistence has actually happened; never a prediction computed ahead of the real write. */
  isNewBest: boolean;
}

/**
 * The one canonical best-run record `EndlessStorage` persists - always
 * from a single real run, never a combination of independently-best
 * Length/Score/time from different runs. `survivalTimeMs` is stored only
 * as contextual information from that run - see `EndlessStorage`'s
 * comparator doc for why it is never itself a tie-break.
 */
export interface EndlessBestRecord {
  length: number;
  score: number;
  survivalTimeMs: number;
}

/**
 * What `GameScene` hands `EndlessResultScene` once a run has ended - the
 * single dedicated Endless result data shape, mirroring
 * `DailyResultSceneData`'s role but with nothing Daily-specific.
 */
export interface EndlessResultSceneData {
  outcome: EndlessRunOutcome;
  /**
   * The exact submission payload built at the moment this run ended -
   * absent/`null` only for a QA preview (`qaPreviewEndlessResult()`),
   * which must never be submitted. `EndlessResultScene` (not `GameScene`)
   * owns actually attempting this submission - see
   * `src/endless/onboardingSubmission.ts` - because a first-time Endless
   * player answers the Online Features prompt and (if needed) sets a
   * display name on THIS screen, after `GameScene` has already handed
   * off and moved on. Carrying the already-built metrics here is what
   * lets that later ENABLE/name-save actually submit the run that just
   * ended, instead of it being silently discarded because Online
   * Features happened to be off at the exact instant the run finished.
   */
  metrics?: EndlessSubmissionMetrics | null;
  /** Instant Play & Onboarding V1 - analytics-only (`worlds_discovery_clicked`'s `result_source`), never affects submission/persistence. `"instant_play"` only when `EntryScene`'s PLAY NOW launched this run; absent for every other Endless entry (Main Menu, PLAY AGAIN). */
  entrySource?: "instant_play";
}

/**
 * Defines leaderboard comparability for a completed Endless run: arena size/
 * layout, starting configuration, starting speed, permanent speed cap,
 * Growth/RUSH behavior, fruit rules, scoring behavior - anything that
 * affects whether two runs' Length/Score are fair to rank against each
 * other. Bump this whenever a future Endless change affects leaderboard
 * fairness (including a scoring change) - never reuse or silently
 * reinterpret an old value. A rules-version bump never deletes or
 * overwrites historical rows (see `supabase/migrations/007_endless_leaderboard.sql`'s
 * `(player_id, endless_rules_version)` composite primary key) - the
 * default leaderboard simply displays only the current version.
 */
/**
 * PR26: bumped 1 -> 2. This PR changes Growth/RUSH scoring behavior (the
 * RUSH Score combo table), Skill Score behavior (LAST-SECOND TURN's
 * trigger and base reward), and a Length reward (SPEED TRIAL's completion
 * growth, 2 -> 3) - all explicitly named above as things that affect
 * leaderboard fairness. Old version-1 rows are untouched (still queryable
 * under `endless_rules_version = 1`, just no longer the default/displayed
 * version) - see `supabase/migrations/011_endless_rules_v2.sql`, which
 * adds the new `p_endless_rules_version = 2` branch to
 * `record_endless_result()` (same arena/length bounds as v1 - only the
 * scoring inputs changed, not the arena) without touching the v1 branch.
 */
export const ENDLESS_RULES_VERSION = 2;

/**
 * Which verification METHOD the server actually applied to a stored
 * submission - audit metadata, never something the client claims. V1 is
 * "aggregate counters + strict bounds + internal-consistency/plausibility
 * checks" (`src/endless/validation.ts`, `netlify/functions/endless-result.ts`)
 * - explicitly not deterministic replay, not a server-issued seed, not an
 * event transcript, and never described as proof a real run occurred. A
 * future move to a stronger verification method (e.g. an event transcript)
 * would bump this independently of `ENDLESS_RULES_VERSION` (which is about
 * gameplay/scoring comparability, not how a submission was checked) and
 * `scoringVersion` (which is about the score formula itself).
 */
export const ENDLESS_VERIFICATION_VERSION = 1;

/**
 * The complete, submitted-once-per-run aggregate metrics/proof shape for
 * the Endless global leaderboard - deliberately its own type, never
 * Campaign's level/world/stars-shaped `RunSubmission` (`src/online/types.ts`)
 * with fake values forced into it. Built by `buildEndlessSubmissionMetrics()`
 * (`src/endless/submissionMetrics.ts`) from `RunTracker`'s aggregate
 * counters and the engine's own `LevelGameSnapshot` - never a per-move/
 * per-fruit history, never PII. This is "validated"/"best-effort validated"
 * evidence (aggregate counters + strict bounds + internal-consistency/
 * plausibility checks - see `src/endless/validation.ts`), explicitly never
 * proof a real run occurred - the same practical verification bar Campaign
 * (`submit_run`) and Daily (`record_daily_result`) already ship.
 */
export interface EndlessSubmissionMetrics {
  runId: string;
  endlessRulesVersion: number;
  scoringVersion: number;
  endReason: EndlessEndReason;
  /** Present only for `reason: "death"` - `null` for "quit"/"arena_cleared". */
  deathReason: "wall" | "obstacle" | "self" | null;
  finalLength: number;
  /** `biteScore + skillScore` - the exact same "Final Score" the HUD/result screen show. */
  finalScore: number;
  survivalTimeMs: number;
  /** The engine's raw fruit/CHAOS-derived score (`LevelGameSnapshot.score`) - never itself the displayed Score. */
  biteScore: number;
  /** Sum of every credited skill moment's awarded points (`LevelGameSnapshot.skillScore`). */
  skillScore: number;
  safeCount: number;
  rushCount: number;
  chaosCount: number;
  jackpotCount: number;
  wormholeCount: number;
  shrinkCount: number;
  speedTrialStartedCount: number;
  /** Successfully completed SPEED TRIAL count - never exceeds `speedTrialStartedCount`. */
  speedTrialSurvivedCount: number;
  /** Must always be 0 under current Endless rules (v1's CHAOS pool never selects SCORE HIT) - see `GameConfig.speedEngine`/`GameConfig.bites.chaos.outcomeWeightsCurrentV2`. */
  scoreHitCount: number;
  /** The highest consecutive-RUSH combo streak reached this run (`RunTracker.getBiteCounts().maxRushStreak`) - never exceeds `rushCount`. */
  maxRushCombo: number;
  /** The fastest (smallest) movement interval actually reached this run, in ms - never below the permanent 75ms Endless cap, never above the 165ms starting speed. */
  fastestStepMs: number;
  moveCount: number;
  perfectFoldCount: number;
  lastSecondTurnCount: number;
  tailGrazeCount: number;
  threadTheNeedleCount: number;
  doubleDangerCount: number;
  bestFlowStreak: number;
  trickBiteCount: number;
  clutchBiteCount: number;
  pauseCount: number;
  pausedDurationMs: number;
  /** PR34: this run's frozen Mobile/Desktop category (`detectPlatform()` at run start) - decides which Endless board the result lands on. Always set by this client build; optional only because a run queued by a pre-PR34 build never had one (stored as `unknown`). */
  platform?: Platform;
}

/** One row of the public Endless leaderboard (`get_endless_leaderboard()`). Never carries a player id - see that RPC's own doc. Survival time/end reason are informational/display only, never ranking inputs. */
export interface EndlessLeaderboardEntry {
  rank: number;
  displayName: string;
  length: number;
  score: number;
  survivalTimeMs: number;
  endReason: EndlessEndReason;
}

/** The signed-in caller's own current Endless rank (`get_my_endless_rank()`), even when outside the Top 100 the public leaderboard returns. `hasResult: false` means no row exists yet for the requested rules version - `rank`/`length`/`score`/`survivalTimeMs`/`endReason` are then meaningless placeholders, never a real 0th-place result. */
export interface EndlessMyRank {
  hasResult: boolean;
  rank: number | null;
  length: number;
  score: number;
  survivalTimeMs: number;
  endReason: EndlessEndReason | null;
}
